import { createEntityAdapter, EntityState, EntityAdapter } from '@ngrx/entity';

import {
  StateWithStatus,
  getInitialStateWithStatus,
  toggleStatusSuccess,
  PaginationState,
  toggleStatusLoading
} from 'app/helpers';

import {
  OrderCardNumber,
  TrackingPageNumber
} from './tracking-page-number.model';
import { TrackingPageNumbersActions } from './tracking-page-numbers.actions';
import { TrackingPageActions } from '../tracking-page/tracking-page.actions';
import { TrackingPageActionTypes } from '../tracking-page/tracking-page.actions';
import { TrackingPageNumbersTypes as types } from './tracking-page-numbers.actions';

export interface SidebarItem {
  id: any;
  display: any;
  items: TrackingPageNumber[];
}

export const adapter: EntityAdapter<OrderCardNumber> = createEntityAdapter();

export interface TrackingPageNumbersState
  extends EntityState<OrderCardNumber>,
    PaginationState,
    StateWithStatus {
  showDetails: string[];
  showDetailsLoading: string[];
  sidebarItems: SidebarItem[];
  totalSidebarItems: number;
  openedSidebarItems: any[];
  selectedTrackingNumber: any;
  totalItems: number;
  trackingNumberCheckpoints: any;
}

export const initialState: TrackingPageNumbersState = getInitialStateWithStatus(
  adapter.getInitialState({
    pagination: {},
    showDetails: [],
    showDetailsLoading: [],
    sidebarItems: [],
    totalSidebarItems: 0,
    openedSidebarItems: [],
    selectedTrackingNumber: null,
    totalItems: 0,
    trackingNumberCheckpoints: {}
  })
);

export const trackingPageNumbersReducer = (
  state: TrackingPageNumbersState = initialState,
  action: TrackingPageActions | TrackingPageNumbersActions
) => {
  switch (action.type) {
    case types.ADD_TRACKING_PAGE_NUMBERS: {
      return adapter.setAll(action.payload.items, {
        ...toggleStatusSuccess(state),
        totalItems: action.payload.totalItems
      });
    }
    case types.LOAD_MORE_TRACKING_NUMBERS:
      return toggleStatusLoading(state);
    case types.UPDATE_REVIEW: {
      const entitiesForUpdate: { id: string; changes }[] = [];
      Object.keys(state.entities).forEach((orderId) => {
        let hasChanges = false;
        const trackingNumbersTmp = state.entities[orderId].trackingNumbers.map(
          (trackingNumber) => {
            if (trackingNumber.id === action.payload.shipmentId) {
              hasChanges = true;
              return {
                ...trackingNumber,
                review: { ...action.payload, id: action.payload.shipmentId }
              };
            }
            return trackingNumber;
          }
        );

        if (hasChanges) {
          entitiesForUpdate.push({
            id: orderId,
            changes: { trackingNumbers: trackingNumbersTmp }
          });
        }
      });

      return adapter.updateMany(entitiesForUpdate, state);
    }
    case types.OPEN_TRACKING_NUMBER_DETAILS: {
      const { id, orderId } = action.payload;
      return {
        ...state,
        showDetails: [...state.showDetails, id + orderId],
        showDetailsLoading: [...state.showDetailsLoading, id + orderId]
      };
    }
    case types.SET_MORE_TRACKING_NUMBERS: {
      return adapter.setAll(action.payload.items, {
        ...toggleStatusSuccess(state),
        totalItems: action.payload.totalItems
      });
    }
    case types.SET_TRACKING_NUMBER_CHECKPOINTS: {
      const { id, checkpoints, orderId } = action.payload;
      return {
        ...state,
        showDetailsLoading: state.showDetailsLoading.filter(
          (cId) => cId !== id + orderId
        ),
        trackingNumberCheckpoints: {
          ...state.trackingNumberCheckpoints,
          [id]: checkpoints
        }
      };
    }
    case types.CLOSE_TRACKING_NUMBER_DETAILS: {
      return {
        ...state,
        showDetails: state.showDetails.filter((id) => id !== action.payload)
      };
    }
    case types.CLOSE_ALL_TRACKING_NUMBER_DETAILS: {
      return {
        ...state,
        showDetails: []
      };
    }
    case types.OPEN_SIDEBAR_ITEM: {
      return {
        ...toggleStatusLoading(state),
        openedSidebarItems: [action.payload]
      };
    }
    case types.CLOSE_SIDEBAR_ITEM: {
      return adapter.removeAll({
        ...state,
        openedSidebarItems: [],
        totalItems: 0
      });
    }
    case types.SELECT_TRACKING_NUMBER: {
      return {
        ...state,
        selectedTrackingNumber: action.payload
      };
    }
    case TrackingPageActionTypes.BOOTSTRAP: {
      return toggleStatusLoading(state);
    }
    case TrackingPageActionTypes.BOOTSTRAP_SUCCESS: {
      const { mainItems, sidebarItems } = action.payload;
      return adapter.setAll(
        mainItems.items,
        toggleStatusSuccess({
          ...state,
          totalItems: mainItems.totalItems,
          totalSidebarItems: sidebarItems.totalItems
            ? sidebarItems.totalItems
            : 0,
          sidebarItems: sidebarItems.items ? [...sidebarItems.items] : [],
          openedSidebarItems:
            sidebarItems.items && sidebarItems.items.length
              ? [sidebarItems.items[0].id]
              : [],
          pagination: {
            ...state.pagination,
            totalItems: mainItems.totalItems
          }
        })
      );
    }

    case types.SET_SIDEBAR_ITEMS: {
      return {
        ...state,
        sidebarItems: action.payload.items
      };
    }
    default: {
      return state;
    }
  }
};
